import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Layout, Video, Layers, BookOpen, GraduationCap, 
  Zap, TrendingUp, Palette, ArrowRight, Check, ChevronDown 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { featuredProduct, categories, whyOrvDigital, faqs, trustItems } from '../mock';
import api from '../lib/api';

// Icon mapping
const iconMap = {
  Sparkles,
  Layout,
  Video,
  Layers,
  BookOpen,
  GraduationCap,
  Zap,
  TrendingUp,
  Palette
};

// Kinetic Hero with Line-by-Line Reveal
const KineticHero = () => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const lineVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
      clipPath: 'inset(0 0 100% 0)'
    },
    visible: {
      opacity: 1,
      y: 0,
      clipPath: 'inset(0 0 0% 0)',
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const subtextVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 1
      }
    }
  };

  const ctaVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 1.3
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#050506]">
      {/* Animated Grid Background with Glow */}
      <div className="absolute inset-0 opacity-30">
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: `
              linear-gradient(rgba(108, 43, 255, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(108, 43, 255, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
            backgroundPosition: 'center center'
          }}
        />
      </div>

      {/* Purple Glow Effects */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.15, scale: 1 }}
        transition={{ duration: 2, ease: 'easeOut' }}
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600 rounded-full blur-[120px]"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{ duration: 2, delay: 0.3, ease: 'easeOut' }}
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-[120px]"
      />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-12 md:py-16 lg:py-24">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 mt-6 md:mt-0"
        >
          <span className="inline-block px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-xs font-medium text-gray-300 tracking-wider uppercase">
            Premium AI & Digital Resources
          </span>
        </motion.div>

        {/* Kinetic Headline with Line-by-Line Reveal */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
          className="mb-8 overflow-hidden"
        >
          <motion.h1 
            variants={lineVariants}
            className="text-7xl md:text-8xl lg:text-9xl font-bold text-white mb-2"
          >
            Create.
          </motion.h1>
          <motion.h1 
            variants={lineVariants}
            className="text-7xl md:text-8xl lg:text-9xl font-bold text-white mb-2"
          >
            Build.
          </motion.h1>
          <motion.h1 
            variants={lineVariants}
            className="text-7xl md:text-8xl lg:text-9xl font-bold text-white"
          >
            Grow.
          </motion.h1>
        </motion.div>

        {/* Supporting Headline */}
        <motion.h2
          variants={subtextVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
          className="text-2xl md:text-3xl lg:text-4xl font-medium text-gray-300 mb-6"
        >
          Digital resources built for your next big idea.
        </motion.h2>

        {/* Supporting Text */}
        <motion.p
          variants={subtextVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
          className="text-base md:text-lg text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed"
        >
          Discover premium AI resources, templates, creator tools, digital bundles and practical 
          resources designed to help you create smarter, build faster and grow online.
        </motion.p>

        {/* CTAs */}
        <motion.div
          variants={ctaVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={() => navigate('/products')}
              className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-semibold px-8 py-6 rounded-xl text-base shadow-2xl shadow-purple-500/30 hover:shadow-purple-500/50 transition-all duration-300 group"
            >
              Explore Products
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </motion.div>

          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={() => navigate('/about')}
              variant="outline"
              className="border-2 border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10 backdrop-blur-sm text-white font-semibold px-8 py-6 rounded-xl text-base transition-all duration-300"
            >
              Discover OrvDigital
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ChevronDown className="w-6 h-6 text-gray-500" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Trust Strip
const TrustStrip = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const iconComponents = {
    Zap,
    CreditCard: ({ className }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="1" y1="10" x2="23" y2="10" strokeWidth="2"/>
      </svg>
    ),
    Users: ({ className }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="9" cy="7" r="4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    Shield: ({ className }) => (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  };

  return (
    <section ref={ref} className="py-12 bg-gradient-to-b from-[#050506] to-[#0B0B0D] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {trustItems.map((item, index) => {
            const Icon = iconComponents[item.icon] || Zap;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex flex-col items-center text-center group"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="mb-3 p-3 bg-white/5 rounded-lg group-hover:bg-white/10 transition-colors duration-300"
                >
                  <Icon className="w-6 h-6 text-purple-400" />
                </motion.div>
                <p className="text-sm font-medium text-gray-300">{item.text}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// Featured Product (real, from API)
const FeaturedProduct = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get('/products', { params: { featured: true } })
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    if (products.length <= 1 || paused) return;

    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % products.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [products.length, paused]);

  if (!products.length) return null;

  const product = products[current];
  const price = product.licenses?.creator?.price;

  const nextSlide = () => {
    setPaused(true);
    setCurrent((prev) => (prev + 1) % products.length);
  };

  const prevSlide = () => {
    setPaused(true);
    setCurrent((prev) =>
      prev === 0 ? products.length - 1 : prev - 1
    );
  };

  return (
    <section
      ref={ref}
      className="py-10 md:py-14 bg-[#0B0B0D] relative overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-8 md:mb-10">
          <span className="inline-block px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs font-medium text-purple-300 tracking-wider uppercase">
            Explore
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-3">
            Featured Digital Resources
          </h2>

          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            Practical resources designed to help you move from idea to execution.
          </p>
        </div>

        <motion.div
          key={product.id || product.slug}
          initial={{ opacity: 0, x: 25 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          drag={products.length > 1 ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={(e, info) => {
            if (info.offset.x < -60) nextSlide();
            if (info.offset.x > 60) prevSlide();
          }}
          className="max-w-5xl mx-auto"
        >
          <div className="relative bg-gradient-to-b from-white/5 to-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">

            {product.badge && (
              <span className="absolute z-20 top-4 left-4 px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
                {product.badge}
              </span>
            )}
<div
  onClick={() => navigate(`/products/${product.slug}`)}
  className="md:hidden relative aspect-square cursor-pointer overflow-hidden"
>
  <img
    src={product.cover_image}
    alt={product.name}
    className="w-full h-full object-cover"
  />

  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

  {product.badge && (
    <span className="absolute top-4 left-4 px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
      {product.badge}
    </span>
  )}

  <div className="absolute bottom-0 left-0 right-0 p-5">
    <h3 className="text-2xl font-bold text-white leading-tight">
      {product.name}
    </h3>
  </div>
</div>
            <div className="hidden md:grid md:grid-cols-2">

              <div className="relative aspect-square sm:aspect-[4/3] md:aspect-auto md:h-full md:min-h-[340px] overflow-hidden bg-purple-900/10">
                <img
                  src={product.cover_image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D]/60 to-transparent" />
              </div>

              <div className="p-6 md:p-8 flex flex-col justify-center">
                <span className="text-sm font-medium text-purple-400 mb-3">
                  {product.category_name}
                </span>

                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">
                  {product.name}
                </h3>

                <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-5">
                  {product.short_description}
                </p>

                <div className="space-y-2 mb-6">
                  {(product.highlights || []).slice(0, 2).map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 text-sm text-gray-300"
                    >
                      <Check className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <Button
                    onClick={() => navigate(`/products/${product.slug}`)}
                    className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-semibold"
                  >
                    View Product
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>

                  {price && (
                    <p className="text-xl font-bold text-white">
                      From ₹{price}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {products.length > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">

            <button
              onClick={prevSlide}
              className="w-10 h-10 rounded-full border border-white/10 bg-white/5 text-white hover:bg-purple-600 transition"
              aria-label="Previous featured product"
            >
              ‹
            </button>

            <div className="flex gap-2">
              {products.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setPaused(true);
                    setCurrent(index);
                  }}
                  className={`h-2 rounded-full transition-all ${
                    current === index
                      ? "w-6 bg-purple-500"
                      : "w-2 bg-white/30"
                  }`}
                  aria-label={`Go to featured product ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="w-10 h-10 rounded-full border border-white/10 bg-white/5 text-white hover:bg-purple-600 transition"
              aria-label="Next featured product"
            >
              ›
            </button>

          </div>
        )}
      </div>
    </section>
  );
};

// Categories Section
const CategoriesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-12 md:py-16 lg:py-24 bg-gradient-to-b from-[#0B0B0D] to-[#050506]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Everything You Need to Create & Grow
          </h2>
        </motion.div>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {categories.map((category, index) => {
            const Icon = iconMap[category.icon];
            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                className="group p-4 md:p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/[0.07] hover:border-purple-500/30 transition-all duration-300 cursor-pointer"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                  className="mb-3 md:mb-6 inline-block p-3 md:p-4 bg-purple-500/10 rounded-xl group-hover:bg-purple-500/20 transition-colors duration-300"
                >
                  <Icon className="w-5 h-5 md:w-7 md:h-7 text-purple-400" />
                </motion.div>
                <h3 className="text-base md:text-xl font-bold text-white mb-1 md:mb-3 group-hover:text-purple-300 transition-colors duration-300">
                  {category.name}
                </h3>
                <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                  {category.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Why OrvDigital - Numbered Manifesto
const WhySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-12 md:py-16 lg:py-24 bg-[#0B0B0D] relative overflow-hidden">
      {/* Background Effect */}
      <div className="absolute inset-0 opacity-30">
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(108, 43, 255, 0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <span className="inline-block px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs font-medium text-purple-300 tracking-wider uppercase mb-6">
            Why OrvDigital
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Digital resources. Real possibilities.
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-10 md:mb-16">
            Everything we create is built around one simple idea — helping you turn ideas into action.
          </p>
        </motion.div>

        {/* Numbered Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
          {whyOrvDigital.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -4, scale: 1.02 }}
              className="group relative p-4 md:p-8 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl hover:border-purple-500/30 transition-all duration-300 overflow-hidden"
            >
              {/* Number */}
              <div className="absolute top-0 right-0 text-5xl md:text-8xl font-bold text-white/5 leading-none pr-3 md:pr-4 pt-1 md:pt-2 group-hover:text-purple-500/10 transition-colors duration-300">
                {item.number}
              </div>

              <div className="relative z-10">
                <div className="text-xl md:text-2xl font-bold text-purple-400 mb-2 md:mb-4">{item.number}</div>
                <h3 className="text-base md:text-xl font-bold text-white mb-2 md:mb-4 group-hover:text-purple-300 transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, success, error

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      // Placeholder for future integration
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <section ref={ref} className="py-12 md:py-16 lg:py-24 bg-gradient-to-b from-[#050506] to-[#0B0B0D] relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-full h-96 max-w-3xl bg-purple-600/10 blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Stay Ahead.
          </h2>
          <p className="text-lg text-gray-400 mb-12 max-w-2xl mx-auto">
            Get updates on new digital resources, creator tools and OrvDigital releases.
          </p>

          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all duration-300"
              />
              <Button
                type="submit"
                className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-semibold px-8 py-4 rounded-xl shadow-xl shadow-purple-500/20 hover:shadow-purple-500/40 transition-all duration-300 whitespace-nowrap"
              >
                Join OrvDigital
              </Button>
            </div>
            {status === 'success' && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 text-sm text-green-400"
              >
                Thank you for subscribing!
              </motion.p>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-12 md:py-16 lg:py-24 bg-[#0B0B0D]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-400">
            Everything you need to know about OrvDigital
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-6 hover:bg-white/[0.07] hover:border-purple-500/30 transition-all duration-300"
              >
                <AccordionTrigger className="text-left text-white hover:text-purple-300 font-medium py-6 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

// Main HomePage
export default function HomePage() {
  return (
    <div className="bg-[#050506]">
      <KineticHero />
      <TrustStrip />
      <FeaturedProduct />
      <CategoriesSection />
      <WhySection />
      <NewsletterSection />
      <FAQSection />
    </div>
  );
}
